import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { API } from "../lib/api";
import { Stars } from "../components/Stars";

const RateItem = ({ token, item, name }) => {
  const [rating, setRating] = useState(item.rating || 0);
  const [text, setText] = useState(item.text || "");
  const [saved, setSaved] = useState(!!item.rating);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!rating) return toast.error("Tap a star rating first.");
    setBusy(true);
    try {
      await axios.post(`${API}/rate/${token}`, { item: item.id, rating, text: text.trim() || null, display_name: name.trim() || null });
      setSaved(true);
      toast.success("Thank you!");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || "Could not save. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-white/10 p-5" data-testid={`rate-item-${item.id}`}>
      <div className="text-sm text-neutral-100 mb-3">{item.title}</div>
      <Stars value={rating} onChange={(v) => { setRating(v); setSaved(false); }} size={26} testid={`rate-stars-${item.id}`} />
      <textarea rows={2} maxLength={400} value={text} onChange={(e) => { setText(e.target.value); setSaved(false); }}
        aria-label={`What helped in ${item.title}`} placeholder="What helped? Who is this right for? (optional)"
        data-testid={`rate-text-${item.id}`} className="wwr-input w-full rounded-xl px-4 py-3 text-sm mt-3" />
      <button type="button" onClick={save} disabled={busy} data-testid={`rate-save-${item.id}`}
        className="mt-3 btn-primary px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-60">
        {saved ? <><Check size={13} /> Saved</> : busy ? "Saving…" : "Save rating"}
      </button>
    </div>
  );
};

export default function RateToolkits() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [name, setName] = useState("");

  useEffect(() => {
    axios.get(`${API}/rate/${token}`)
      .then(({ data }) => { setData(data); setName((n) => n || data.name || ""); setState("ok"); })
      .catch(() => setState("invalid"));
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="card-surface p-8 md:p-10 max-w-xl w-full space-y-5" data-testid="rate-page">
        {state === "loading" && <Loader2 className="mx-auto text-teal-400 animate-spin" size={32} />}
        {state === "invalid" && <p className="text-center text-neutral-400" data-testid="rate-invalid">This link isn't valid.</p>}
        {state === "ok" && (
          <>
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-teal-300/80 mb-2">Rate your toolkits</div>
              <h1 className="font-display text-3xl text-neutral-100">How did it land?</h1>
              <p className="text-sm text-neutral-400 mt-2">Your rating helps the next person find the toolkit that fits where they are. Written notes appear only if Gabi approves them.</p>
            </div>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} aria-label="Name to show"
              placeholder="Name to show (first name or initials)" data-testid="rate-name" className="wwr-input w-full rounded-xl px-4 py-3 text-sm" />
            {data.items.map((it) => <RateItem key={it.id} token={token} item={it} name={name} />)}
          </>
        )}
        <Link to="/" className="block text-center text-sm text-teal-300 hover:text-teal-200" data-testid="rate-home">Back to Why We Recover</Link>
      </div>
    </div>
  );
}
